import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateChildInput, UpdateChildInput } from "@shared/validation/child.schema";
import { apiClient, ApiError } from "./client";

export interface ChildSummary {
  id: string;
  /** The nickname: what caregivers call the child. Shown as "Nickname". */
  name: string;
  /** Optional full legal name. Hidden on care links unless explicitly shared. */
  legalName: string | null;
  age: number | null;
  dateOfBirth: string | null;
  grade: string | null;
  additionalNotes: string | null;
  isEnrolled: boolean;
  schoolName: string | null;
  schoolPhone: string | null;
  teacherName: string | null;
  schoolNursePhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  secondaryEmergencyContactName: string | null;
  secondaryEmergencyContactPhone: string | null;
  secondaryEmergencyContactRelationship: string | null;
  /**
   * Absent while PHOTO_UPLOADS_ENABLED is false: the server never returns a
   * child's photo. Optional so callers keep compiling either way.
   */
  photoUrl?: string | null;
  /** A photo from before the switch is still stored and can be removed. */
  hasPhoto: boolean;
  createdAt: string;
  updatedAt: string;
}

const CHILDREN_KEY = ["children"] as const;
const childKey = (id: string) => ["children", id] as const;

export function useChildren() {
  return useQuery<ChildSummary[], ApiError>({
    queryKey: CHILDREN_KEY,
    queryFn: () => apiClient<ChildSummary[]>("/api/children"),
  });
}

export function useChild(id: string | undefined) {
  return useQuery<ChildSummary, ApiError>({
    queryKey: id ? childKey(id) : ["children", "__none__"],
    queryFn: () => apiClient<ChildSummary>(`/api/children/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateChild() {
  const queryClient = useQueryClient();
  return useMutation<ChildSummary, ApiError, CreateChildInput>({
    mutationFn: (vars) =>
      apiClient<ChildSummary>("/api/children", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
      queryClient.setQueryData(childKey(created.id), created);
    },
  });
}

export function useUpdateChild(id: string) {
  const queryClient = useQueryClient();
  return useMutation<ChildSummary, ApiError, UpdateChildInput>({
    mutationFn: (vars) =>
      apiClient<ChildSummary>(`/api/children/${id}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(childKey(id), updated);
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}

export function useDeleteChild() {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string, { previous?: ChildSummary[] }>({
    mutationFn: (id) =>
      apiClient<{ deleted: true }>(`/api/children/${id}`, { method: "DELETE" }),
    // Optimistic: drop the child from the list immediately, roll back on error.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: CHILDREN_KEY });
      const previous = queryClient.getQueryData<ChildSummary[]>(CHILDREN_KEY);
      if (previous) {
        queryClient.setQueryData<ChildSummary[]>(
          CHILDREN_KEY,
          previous.filter((c) => c.id !== id),
        );
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(CHILDREN_KEY, ctx.previous);
    },
    onSettled: (_data, _err, id) => {
      queryClient.removeQueries({ queryKey: childKey(id) });
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}

export function useSetChildPhoto(id: string) {
  const queryClient = useQueryClient();
  return useMutation<ChildSummary, ApiError, string>({
    mutationFn: (photoUrl) =>
      apiClient<ChildSummary>(`/api/children/${id}/photo`, {
        method: "PUT",
        body: JSON.stringify({ photoUrl }),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(childKey(id), updated);
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}

export function useClearChildPhoto(id: string) {
  const queryClient = useQueryClient();
  return useMutation<ChildSummary, ApiError, void>({
    mutationFn: () =>
      apiClient<ChildSummary>(`/api/children/${id}/photo`, { method: "DELETE" }),
    onSuccess: (updated) => {
      queryClient.setQueryData(childKey(id), updated);
      queryClient.invalidateQueries({ queryKey: CHILDREN_KEY });
    },
  });
}
